import styled, { css } from 'styled-components';
import { TypographyRootProps } from './ExtTypography.types';
import { getColor } from '../colors';
import omitForwardedProps from '../utils/omitForwardedProps';

const omittedProps: Array<keyof TypographyRootProps> = [
  'align',
  'bMargin',
  'lMargin',
  'rMargin',
  'noWrap',
  'lineThrough',
  'variant',
  'color',
];

const commonStyle = css<TypographyRootProps>`
  font-family: 'Roboto';
  font-style: normal;
  text-align: ${({ align }) => align || 'left'};
  margin-bottom: ${({ bMargin }) => bMargin || 0}px;
  margin-left: ${({ lMargin }) => lMargin || 0}px;
  margin-right: ${({ rMargin }) => rMargin || 0}px;
  margin-top: ${({ tMargin }) => tMargin || 0}px;
  color: ${({ color }) => getColor(color || 'primary1')};
  text-decoration: ${({ lineThrough }) => (lineThrough ? 'line-through' : 'none')};

  ${({ fontWeight }) =>
    fontWeight &&
    css`
      font-weight: ${fontWeight};
    `};

  ${({ textTransform }) =>
    textTransform &&
    css`
      text-transform: ${textTransform};
    `};

  ${({ textDecoration }) =>
    textDecoration &&
    css`
      text-decoration: ${textDecoration};
    `};

  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    `};
`;

const H1Styles = css`
  font-weight: 700;
  font-size: 40px;
  line-height: 47px;
`;

const H2Styles = css`
  font-weight: 600;
  font-size: 30px;
  line-height: 35px;
`;

const H3Styles = css`
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
`;

const H4Styles = css`
  font-weight: 600;
  font-size: 20px;
  line-height: 23px;
`;

const H5Styles = css`
  font-weight: 600;
  font-size: 18px;
  line-height: 21px;
`;

const ParagraphLargeStyles = css`
  font-weight: 500;
  font-size: 18px;
  line-height: 26px;
`;

const ParagraphMediumStyles = css`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

const LabelLargeStyles = css`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
`;

const LabelMediumStyles = css`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

const LabelMediumBoldStyles = css`
  ${LabelMediumStyles};
  font-weight: 700;
`;

const LabelSmallStyles = css`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
`;

const LabelExtraSmallStyles = css`
  font-weight: 400;
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 0.02em;
`;

// ----------------------------------
// ------- Components Header --------

export const H1 = styled.h1.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${H1Styles};
  ${commonStyle};
`;

export const H2 = styled.h2.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${H2Styles};
  ${commonStyle};
`;

export const H3 = styled.h3.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${H3Styles};
  ${commonStyle};
`;

export const H4 = styled.h4.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${H4Styles};
  ${commonStyle};
`;

export const H5 = styled.h5.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${H5Styles};
  ${commonStyle};
`;

// -------------------------------------
// ------- Components Paragraph --------

export const ParagraphLarge = styled.p.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${ParagraphLargeStyles};
  ${commonStyle};
`;

export const ParagraphMedium = styled.p.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${ParagraphMediumStyles};
  ${commonStyle};
`;

// ---------------------------------
// ------- Components Label --------

export const LabelLarge = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelLargeStyles};
  ${commonStyle};
`;

export const LabelMedium = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelMediumStyles};
  ${commonStyle};
`;

export const LabelMediumBold = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelMediumBoldStyles};
  ${commonStyle};
`;

export const LabelSmall = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelSmallStyles};
  ${commonStyle};
`;

export const LabelSmallUppercase = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelSmallStyles};
  ${commonStyle};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const LabelExtraSmall = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelExtraSmallStyles};
  ${commonStyle};
`;

export const LabelExtraSmallUppercase = styled.div.withConfig(
  omitForwardedProps<TypographyRootProps>(omittedProps)
)<TypographyRootProps>`
  ${LabelExtraSmallStyles};
  ${commonStyle};
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

// export const LabelExtraSmallUppercase = styled.div.withConfig(
//   omitForwardedProps<TypographyRootProps>(omittedProps)
// )<TypographyRootProps>`
//   ${LabelExtraSmallUppercaseStyles}
//   ${commonStyle}
// `;
//
// export const LabelExtraSmallUppercaseSemibold = styled.div.withConfig(
//   omitForwardedProps<TypographyRootProps>(omittedProps)
// )<TypographyRootProps>`
//   ${LabelExtraSmallUppercaseSemiboldStyles}
//   ${commonStyle}
// `;
//
// export const LineThrough = styled.div.withConfig(
//   omitForwardedProps<TypographyRootProps>(omittedProps)
// )<TypographyRootProps>`
//   ${commonStyle}
// `;
